import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  GKTextField,
  Grid,
  Switch,
  Typography,
} from "components";
import { useFormik } from "formik";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { microWebsite } from "utils/PortfolioOfferings";

interface PortfolioOfferingsFormValues {
  [key: string]: any;
  isRaAccess: boolean;
  published: boolean;
  domainName: string;
}

const MicroWebsite = () => {
  const { id } = useParams();

  const [expandedMicro, setExpandedMicro] = useState<number>(-1);

  const { data, refetch, isFetched } = useQuery({
    queryKey: ["GET_MICRO_WEBSITE"],
    queryFn: () => axiosInstance.get(`/super_admin/${id}/get_micro_website/`),
  });

  const { mutate } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.patch(`/super_admin/${id}/micro_website/`, data),
    onSuccess: (response) => {
      refetch();
      toast.success(response?.data?.message || "Broker Update Successfully");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const formik = useFormik<PortfolioOfferingsFormValues>({
    enableReinitialize: isFetched,
    initialValues: {
      isRaAccess: data?.data?.data?.isRaAccess || false,
      published: data?.data?.data?.published || false,
      domainName: data?.data?.data?.domainName || "",
    },

    onSubmit: (values) => {
      mutate({ isRaAccess: values.isRaAccess });
    },
  });

  return (
    <form id="micro-website-form" onSubmit={formik.handleSubmit}>
      {microWebsite.map((dataItem, index) => (
        <Accordion
          key={`micro-${index}`}
          disableGutters
          onChange={() => {
            setExpandedMicro(index === expandedMicro ? -1 : index);
          }}
          expanded={index === expandedMicro}
          style={{
            border: `solid 1px var(--neutral-100)`,
            borderRadius: "8px",
            boxShadow: "none",
            background: "none",
          }}
        >
          <AccordionSummary
            expandIcon={<GridExpandMoreIcon />}
            aria-controls={`micro-panel${index + 1}-content`}
            id={`micro-panel${index + 1}-header`}
            sx={{
              "& .MuiAccordionSummary-content": {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              },
            }}
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              textTransform="none"
              color={"var(--primary-900)"}
            >
              {dataItem.mainHeading}
            </Typography>
            {expandedMicro === index && (
              <Button
                type="submit"
                variant="contained"
                size="medium"
                form="micro-website-form"
                onClick={(e) => e.stopPropagation()}
              >
                Save
              </Button>
            )}
          </AccordionSummary>
          <Divider />
          {dataItem.details.map((data, subIndex) => (
            <AccordionDetails
              key={`micro-detail-${subIndex}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Switch
                name={data.key}
                onChange={(event) => {
                  formik.setFieldValue(data.key, event.target.checked);
                }}
                checked={formik.values[data.key] === true}
                disabled={data.key === "published"}
              />
              <Typography
                fontSize={14}
                fontWeight={500}
                textTransform="none"
                color={"var(--black-900)"}
              >
                {data.heading}
              </Typography>
            </AccordionDetails>
          ))}

          <Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
            <Grid item xs={6}>
              <FormControl variant="standard" fullWidth>
                <GKTextField
                  name="domainName"
                  inputLabel="Domain Name"
                  value={formik.values.domainName || ""}
                  onChange={formik.handleChange}
                  disabled
                />
              </FormControl>
            </Grid>
          </Grid>
        </Accordion>
      ))}
    </form>
  );
};

export default MicroWebsite;
